// src/components/RequireAccountActive.tsx
import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Clock, CreditCard } from 'lucide-react';

type GuardedRole = 'consultant' | 'partner' | 'lender';

interface RequireAccountActiveProps {
  children: ReactNode;
  role: GuardedRole;
}

interface AccountStatus {
  diditApproved: boolean;
  hasPaid: boolean;
  manualReviewPending: boolean;
  manualReviewDeclined: boolean;
}

function computeStatus(role: GuardedRole, data: any): AccountStatus {
  const diditStatus = data?.diditVerification?.status;
  const diditApproved = diditStatus === 'Approved';
  const manualReviewStatus = data?.diditVerification?.manualReviewStatus;
  const manualReviewPending = diditStatus === 'In Review' && !manualReviewStatus;
  const manualReviewDeclined =
    diditStatus === 'Declined' || manualReviewStatus === 'declined';

  let hasPaid = true; // lender n'a pas de paiement
  if (role === 'consultant') hasPaid = !!data?.consultantTier;
  if (role === 'partner') hasPaid = data?.partnerSubscription?.status === 'active';

  return { diditApproved, hasPaid, manualReviewPending, manualReviewDeclined };
}

export function RequireAccountActive({ children, role }: RequireAccountActiveProps) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      setStatus(computeStatus(role, snap.exists() ? snap.data() : {}));
      setLoading(false);
    });
    return () => unsub();
  }, [user?.uid, role]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    sessionStorage.setItem('intended_destination', location.pathname);
    return <Navigate to="/login" replace />;
  }

  if (!status) return null;

  // Refusé par Didit ou par le staff — accès bloqué, message clair
  if (status.manualReviewDeclined) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
        <h1 className="text-xl font-bold mb-2">Vérification non approuvée</h1>
        <p className="text-muted-foreground max-w-md">
          Votre demande de vérification n'a pas été approuvée. Contactez le
          support pour plus d'informations.
        </p>
      </div>
    );
  }

  // Vérification Didit pas encore approuvée (et pas en révision)
  if (!status.diditApproved && !status.manualReviewPending) {
    return <Navigate to="/login" replace />;
  }

  // En révision manuelle staff — accès bloqué, écran d'attente
  if (status.manualReviewPending) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center gap-3">
        <Clock className="h-12 w-12 text-amber-500" />
        <h1 className="text-xl font-bold">Vérification en cours de révision</h1>
        <p className="text-muted-foreground max-w-md">
          Notre équipe examine votre profil professionnel. Vous recevrez un
          courriel une fois la révision complétée.
        </p>
      </div>
    );
  }

  // Didit approuvé mais paiement manquant — redirige vers la page de forfaits
  if (!status.hasPaid) {
    const paymentRoute = role === 'consultant' ? '/consultant/payment' : '/partner/plans';
    return <Navigate to={paymentRoute} replace />;
  }

  return <>{children}</>;
}